/** @format */

import {TooltipProps} from 'recharts';

import {TooltipDataList} from '../../model/tooltip';
import {Chart} from '../../model/charts';
import isShowTooltip from './helpers/isShowTooltip';
import {ChartTypeSettings} from '../../model/common';
import {BubbleBox, HeadingBox, HeadingSubText, HeadingText, Root} from './style';
import {Bubble} from './components/Bubble';

type PayloadData = {
  payload: {
    name: string;
    primary: number;
  };
};

export type CustomTooltipProps<ChartId extends string> = {
  allData: TooltipDataList<ChartId>;
  allCharts: Chart<ChartId>[];
  chartIndex: number;
  chartTypeSettings: ChartTypeSettings<ChartId>;
  openedScattersCount: number;
  isCompact?: boolean;
} & TooltipProps<number, string>;

const CustomTooltip = <ChartId extends string>({
  payload,
  allCharts,
  allData,
  chartIndex,
  chartTypeSettings,
  active,
  openedScattersCount,
  isCompact,
}: CustomTooltipProps<ChartId>) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0] as PayloadData;
  const tooltipData = allData[data.payload.name];

  if (!tooltipData) {
    return null;
  }

  const show = isShowTooltip({
    tooltipData,
    charts: allCharts,
    chartIndex,
    openedScattersCount,
  });

  if (!show) {
    return null;
  }

  const {rows, date, periodDays} = tooltipData;

  return (
    <Root>
      <HeadingBox>
        <HeadingText>{date}</HeadingText>
        {periodDays ? <HeadingSubText>{periodDays}</HeadingSubText> : null}
      </HeadingBox>

      <BubbleBox>
        {rows?.map(item => {
          const chartType = chartTypeSettings[item.chartId] || 'primary';
          const chart = allCharts.find(chart => chart.chartId === item.chartId);
          if (!chart) {
            return null;
          }
          const {color, labelType} = chart;
          return (
            <Bubble
              key={item.chartId}
              bgColor={color}
              data={item}
              chartType={chartType}
              labelType={labelType}
              isCompact={isCompact}
              periodDays={periodDays}
            />
          );
        })}
      </BubbleBox>
    </Root>
  );
};

export default CustomTooltip;
